import type { Unit } from '@pharmacia/core'
import { formatPaise } from '@pharmacia/core'

// Numbers, written for an axis and written for a tooltip -- which are not
// the same job. An axis tick has to be short enough not to collide with its
// neighbour; a tooltip is where the exact figure belongs.

const inrCompact = (paise: number): string => {
  const r = paise / 100
  const a = Math.abs(r)
  // Indian units, because this is read in Jodhpur: a lakh is 1,00,000 and
  // a crore is 1,00,00,000. Writing 0.1M here would be correct arithmetic
  // and the wrong language.
  if (a >= 1_00_00_000) return `₹${trim(r / 1_00_00_000)}Cr`
  if (a >= 1_00_000) return `₹${trim(r / 1_00_000)}L`
  if (a >= 1_000) return `₹${trim(r / 1_000)}K`
  return `₹${Math.round(r)}`
}

const trim = (v: number) => {
  const s = v.toFixed(v < 10 ? 1 : 0)
  return s.endsWith('.0') ? s.slice(0, -2) : s
}

const counts = new Intl.NumberFormat('en-IN')

/** Short enough for an axis tick. */
export function tick(value: number, unit: Unit): string {
  switch (unit) {
    case 'money': return inrCompact(value)
    case 'percent': return `${Math.round(value)}%`
    case 'kg': return `${trim(value)} kg`
    case 'years': return `${trim(value)}y`
    case 'qty': return trim(value)
    default: return counts.format(Math.round(value))
  }
}

/** The exact figure, for a tooltip, a direct label or a table cell. */
export function exact(value: number | null, unit: Unit): string {
  if (value === null) return '—'
  switch (unit) {
    case 'money': return formatPaise(value)
    case 'percent': return `${value}%`
    case 'kg': return `${value} kg`
    case 'years': return `${value} years`
    case 'qty': return counts.format(value)
    default: return counts.format(value)
  }
}

/**
 * A rounded upper bound and the ticks beneath it.
 *
 * Axes stop at 1,200 rather than 1,187 because a reader estimates a bar's
 * value against the gridlines, and gridlines at awkward numbers make that
 * arithmetic instead of a glance. Steps are held to 1, 2, 2.5 or 5 times a
 * power of ten for the same reason.
 */
export function niceScale(max: number, target = 4): { max: number; ticks: number[] } {
  if (!isFinite(max) || max <= 0) return { max: 1, ticks: [0, 1] }

  const raw = max / target
  const mag = 10 ** Math.floor(Math.log10(raw))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag

  const top = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6)
  return { max: top, ticks }
}

/**
 * Thins an axis so labels do not overlap.
 *
 * Every tick keeps its gridline; only the text is dropped. A chart of
 * ninety days still reads as ninety days -- it just does not try to write
 * ninety dates along the bottom.
 */
export function everyNth(count: number, room: number): number {
  if (count <= room) return 1
  return Math.ceil(count / Math.max(room, 1))
}
